'use client';

import { tv } from '../_lib/utils';
import type { SanityImage } from '../types';

import { Container } from '../Container';
import { Media } from '../Media';
import { TextSectionBlock, type TextSectionBlockProps } from '../TextSectionBlock';
import { useThemeValue, type Brands, type Themes } from '../Theme';

const styles = tv({
	slots: {
		base: 'grid gap-spacing-lg',
		card: 'bg-background-primary rounded-lg overflow-hidden flex flex-col',
	},
	variants: {
		layout: {
			double: {
				base: 'md:grid-cols-2',
				card: '',
			},
			single: {
				base: '',
				card: 'grid md:grid-cols-2',
			},
		},
	},
});

const contentStyles = tv({
	slots: {
		base: 'p-responsive-4xl',
	},
	variants: {
		layout: {
			double: {},
			single: {
				base: 'md:self-center lg:p-responsive-5xl',
			},
		},
		reverseContent: {
			true: {
				base: 'order-2',
			},
			false: {
				base: 'max-md:mb-auto',
			},
		},
	},
});

const mediaStyles = tv({
	slots: {
		base: 'mt-auto [&>div]:h-full [&_img]:h-full',
	},
	variants: {
		layout: {
			double: {
				base: 'aspect-4/3',
			},
			single: {
				base: 'aspect-square h-full max-w-full',
			},
		},
		reverseContent: {
			true: {
				base: 'order-1',
			},
			false: {},
		},
	},
});

export type LargeCardsItemProps = TextSectionBlockProps & {
	className?: string;
	defaultBrand?: Brands;
	image?: SanityImage;
	video?: string;
};

export type LargeCardsProps = {
	className?: string;
	defaultTheme?: Themes;
	item1: LargeCardsItemProps;
	item2?: LargeCardsItemProps;
	reverseContent?: boolean;
};

export function LargeCards(props: LargeCardsProps) {
	const layout = props.item1 && props.item2 ? 'double' : 'single';
	const reverseContent = props.reverseContent ?? false;
	const theme = props.defaultTheme ?? useThemeValue();
	const { base, card } = styles({ layout });
	const { base: content } = contentStyles({ layout, reverseContent });
	const { base: media } = mediaStyles({ layout, reverseContent });

	return (
		<article className={props.className} data-component='LargeCards'>
			<Container className={base()}>
				<article className={card()} data-theme={theme === 'light' ? 'dark' : 'light'} data-brand={props.item1?.defaultBrand ?? undefined}>
					<TextSectionBlock {...props.item1} className={content()} size='sm' />
					<Media className={media()} image={props.item1.image} video={props.item1.video} />
				</article>
				{props.item2 && (
					<article className={card()} data-theme={theme === 'light' ? 'dark' : 'light'} data-brand={props.item2?.defaultBrand ?? undefined}>
						<TextSectionBlock {...props.item2} className={content()} size='sm' />
						<Media className={media()} image={props.item2.image} video={props.item2.video} />
					</article>
				)}
			</Container>
		</article>
	);
}
